import React from 'react';
import { Table } from 'antd';
import { useTranslation } from 'react-i18next';
import SectionWrapper from '@/components/Dispute/SectionWrapper';
import LocalDate from '@/components/atoms/LocaleDate';
import InfoText from '@/components/atoms/InfoText';
import { useGetDisputeActionsPreviewQuery } from '@/store/services/disputeService';
import Helpers from '@/lib/helpers';
import LoaderSpin from '@/components/atoms/LoaderSpin';
import { useParams } from 'react-router-dom';

export default function ActionsPreview() {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const params = useParams();
  const disputeId = params?.disputeId;

  const { data, isLoading } = useGetDisputeActionsPreviewQuery(
    { disputeId },
    { skip: !disputeId }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6 bg-white dark:bg-black-900 border rounded-xl">
        <LoaderSpin />
      </div>
    );
  }

  const payload = data?.payload;

  if (!payload?.arbitrationSummary && !payload?.negotiationSummary) {
    return null;
  }

  const columns = [
    { title: '', dataIndex: 'name', key: 'name' },
    {
      title: '',
      dataIndex: 'value',
      key: 'value',
      className:
        'text-end font-semibold text-light-text-primary dark:text-dark-text-primary',
    },
  ];

  const adminDataSource = [
    {
      name: t('components.disputeFeature.actionTaken'),
      value: payload?.arbitrationSummary?.actionTaken,
    },
    {
      name: t('components.disputeFeature.deductionAmount', 'Deduction amount'),
      value: payload?.arbitrationSummary?.deductionAmount
        ? `${Helpers.formatMoney(Number(payload.arbitrationSummary.deductionAmount))} ${t('SAR')}`
        : null,
    },
    {
      name: t('components.disputeFeature.amountToSeller', 'Amount to seller'),
      value: payload?.arbitrationSummary?.amountToSeller,
    },
    {
      name: t(
        'components.disputeFeature.deductionFromProductAmount',
        'Deduction from product amount'
      ),
      value: !!payload?.arbitrationSummary?.deductionFromProductAmount
        ? `${payload?.arbitrationSummary?.deductionFromProductAmount} %`
        : null,
    },
    {
      name: t(
        'components.disputeFeature.discountOfShipmentFee',
        'Discount of shipment fee'
      ),
      value: payload?.arbitrationSummary?.discountOfShipmentFee,
    },
    {
      name: t(
        'components.disputeFeature.compensationAmount',
        'Compensation amount'
      ),
      value: payload?.arbitrationSummary?.compensationAmount,
    },
    {
      name: t('components.disputeFeature.fundedBy'),
      value: payload?.arbitrationSummary?.fundedBy,
    },
    {
      name: t('components.disputeFeature.actionDate'),
      value: payload?.arbitrationSummary?.actionDate ? (
        <LocalDate
          date={payload.arbitrationSummary.actionDate}
          formatStr="dd MMM yyyy | hh:mm a"
          className="!font-semibold !text-light-text-primary dark:!text-dark-text-primary"
        />
      ) : null,
    },
  ];

  const sellerDataSource = [
    {
      name: t('components.disputeFeature.actionTaken'),
      value: payload?.negotiationSummary?.actionTaken,
    },
    {
      name: t('components.disputeFeature.amount'),
      value: payload?.negotiationSummary?.amount
        ? `${Helpers.formatMoney(payload.negotiationSummary.amount)} ${t('SAR')}`
        : null,
    },
    {
      name: t('components.disputeFeature.fundedBy'),
      value: payload?.negotiationSummary?.fundedBy,
    },
    {
      name: t('buyerAction'),
      value: language?.startsWith('ar')
        ? data?.payload?.negotiationSummary?.buyerAction?.nameAr
        : data?.payload?.negotiationSummary?.buyerAction?.nameEn,
    },
    {
      name: t('components.disputeFeature.actionDate'),
      value: payload?.negotiationSummary?.actionDate ? (
        <LocalDate
          date={payload.negotiationSummary.actionDate}
          formatStr="dd MMM yyyy | hh:mm a"
          className="!font-semibold !text-light-text-primary dark:!text-dark-text-primary"
        />
      ) : null,
    },
  ];

  return (
    <SectionWrapper
      title={
        !!data?.payload?.arbitrationSummary
          ? t('components.disputeFeature.arbitrationSummary')
          : ''
      }
      className="border-[#F58F29] !bg-[#FEF6F2]"
    >
      <div className="space-y-4">
        {/* Admin Actions */}
        {!!payload?.arbitrationSummary && (
          <div className="space-y-[6px]">
            <Table
              columns={columns}
              className="customTable customTable__oranged"
              dataSource={adminDataSource.filter(
                (i) => i.value !== null && i.value !== undefined
              )}
              pagination={false}
              showHeader={false}
              size="middle"
              rowKey="name"
            />
            <MsgPreview
              label={t('components.disputeFeature.arbitrationStatement')}
              value={payload?.arbitrationSummary?.arbitrationStatement}
            />
          </div>
        )}

        {!!payload?.arbitrationSummary && !!payload?.negotiationSummary && (
          <hr className="border-gray-200 dark:border-gray-700" />
        )}

        {/* Seller Actions */}
        {!!payload?.negotiationSummary && (
          <div className="space-y-5">
            <InfoText
              text={t(
                'components.disputeFeature.negotiationSummary',
                'Negotiation summary'
              )}
              className="!text-lg !text-light-text-primary !font-bold dark:!text-dark-text-primary"
            />
            <div>
              <Table
                columns={columns}
                className="customTable customTable__oranged"
                dataSource={sellerDataSource.filter(
                  (e) => e.value !== null && e.value !== undefined
                )}
                pagination={false}
                showHeader={false}
                size="middle"
                rowKey="name"
              />
              <MsgPreview
                value={payload?.negotiationSummary?.msg}
                label={t('components.disputeFeature.message')}
              />
            </div>
          </div>
        )}
      </div>
    </SectionWrapper>
  );
}

export const MsgPreview = ({
  label = 'message',
  value,
}: {
  label?: string;
  value?: string;
}) => {
  if (!value) return null;
  return (
    <div className="mt-2">
      <InfoText
        text={label}
        className="!font-semibold !text-light-text-primary dark:!text-dark-text-primary"
      />
      <InfoText text={value} />
    </div>
  );
};
